import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'

import { Button } from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { addStyle, clearStyles, removeStyle } from 'Pods/SearchPage/Store/actions/styles'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'
import I18n from 'Pods/Shared/I18n'

import { getStylesForAssetType } from './mock'
import FilterSearchInput from '../../../Shared/FilterSearchInput/FilterSearchInput'
import FiltersWrapper from 'Pods/Shared/FiltersWrapper/FiltersWrapper'

export const SEARCH_FOCUS_DELAY = 300

const t = (key, options) => I18n.t(key, { ns: 'rails', ...options })

function Style() {
  const searchRef = useRef(null)
  const focusTimerRef = useRef(null)
  const assetType = useSearchPageStore(state => state.assetType)
  const selectedStyles = useSearchPageStore(state => state.styles)
  const [searchTerm, setSearchTerm] = useState('')
  // Initialized with current assetType so the effect skips on mount; only fires on real changes.
  const previousAssetTypeRef = useRef(assetType)

  useEffect(() => {
    if (previousAssetTypeRef.current === assetType) return
    previousAssetTypeRef.current = assetType
    setSearchTerm('')
    clearStyles()
  }, [assetType])

  useEffect(() => {
    return () => {
      if (focusTimerRef.current) clearTimeout(focusTimerRef.current)
    }
  }, [])

  const availableStyles = useMemo(() => {
    const all = getStylesForAssetType(assetType)
    if (!searchTerm) return all
    const normalized = searchTerm.toLowerCase()
    return all.filter(style => style.toLowerCase().includes(normalized))
  }, [assetType, searchTerm])

  const selectedSet = useMemo(() => new Set(selectedStyles), [selectedStyles])
  const hasSelectedAnyFilters = selectedStyles.length > 0
  const placeholder = hasSelectedAnyFilters
    ? t('search_page.filters.style.title_with_count', { count: selectedStyles.length })
    : t('search_page.filters.style.title')

  const handleToggle = (style) => {
    if (selectedSet.has(style)) {
      removeStyle(style)
    } else {
      addStyle(style)
    }
  }

  const handleOpenChange = useCallback((isOpen) => {
    // Cancel any pending focus first so a fast close-before-delay doesn't steal focus.
    if (focusTimerRef.current) clearTimeout(focusTimerRef.current)
    if (!isOpen || !searchRef.current) return
    // Wait past ClosingDropdown's 200ms open animation before focusing;
    // matches FileFormat.jsx.
    focusTimerRef.current = setTimeout(() => {
      searchRef.current?.focus()
    }, SEARCH_FOCUS_DELAY)
  }, [])

  const content = (
    <div className="style-filter">
      <FilterSearchInput
        id="style-filter-search"
        value={searchTerm}
        onChange={setSearchTerm}
        placeholder={t('search_page.filters.style.find_placeholder')}
        inputRef={searchRef}
      />
      {availableStyles.length > 0 ? (
        <div
          className="style-filter__tags"
          role="group"
          aria-label={t('search_page.filters.style.tags_group_label')}
        >
          {availableStyles.map(style => (
            <Button
              key={style}
              type="secondary"
              size="tiny"
              isActive={selectedSet.has(style)}
              aria-pressed={selectedSet.has(style)}
              onClick={() => handleToggle(style)}
              customClass="style-filter__tag"
            >
              {style}
            </Button>
          ))}
        </div>
      ) : (
        <span className="style-filter__empty cgt-body-base">
          {t('search_page.filters.style.no_matches')}
        </span>
      )}
      <Button
        type="ghost-primary"
        size="small"
        customClass="filter-wrapper__reset-button filter-wrapper__reset-button--inline"
        onClick={clearStyles}
        disabled={!hasSelectedAnyFilters}
      >
        {t('search_page.filters.style.reset')}
      </Button>
    </div>
  )

  return (
    <FiltersWrapper
      placeholder={placeholder}
      content={content}
      onOpenChange={handleOpenChange}
      hasSelectedAnyFilters={hasSelectedAnyFilters}
      closingDropdownClassName="filter-wrapper"
    />
  )
}

export default Style
